import * as React from "react";
import { cx } from "../internal/cx.js";

export type DateInfoCardVariant = "default" | "accent" | "past";

export interface DateInfoCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Required short label ("Enrollment Period Ends"). */
  label: React.ReactNode;
  /** The headline value, already formatted by the parent ("Sep 15, 2026"). */
  value: React.ReactNode;
  /** Optional secondary line ("in 12 days", "tentative"). */
  caption?: React.ReactNode;
  /** Optional leading icon (lucide). */
  icon?: React.ReactNode;
  /** Color variant. Default 'default'. */
  variant?: DateInfoCardVariant;
  /** Trailing action node (link, badge). Rendered in the top-right. */
  action?: React.ReactNode;
}

/**
 * Small bordered tile that highlights a single labeled date or date-like
 * value with an optional secondary line. Presentational only — formatting,
 * relative captions, and tone selection are owned by the parent.
 */
export const DateInfoCard = React.forwardRef<HTMLDivElement, DateInfoCardProps>(
  function DateInfoCard(
    { action, caption, className, icon, label, value, variant = "default", ...props },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cx("cw-v2-date-info-card", `cw-v2-date-info-card--${variant}`, className)}
        {...props}
      >
        <div className="cw-v2-date-info-card__header">
          {icon ? (
            <span aria-hidden className="cw-v2-date-info-card__icon">
              {icon}
            </span>
          ) : null}
          <span className="cw-v2-date-info-card__label">{label}</span>
          {action ? <span className="cw-v2-date-info-card__action">{action}</span> : null}
        </div>
        <div className="cw-v2-date-info-card__value">{value}</div>
        {caption ? <div className="cw-v2-date-info-card__caption">{caption}</div> : null}
      </div>
    );
  },
);
DateInfoCard.displayName = "DateInfoCard";
